/**
 * Sentry Error Tracking Configuration
 * 
 * To enable Sentry:
 * 1. Create account at https://sentry.io
 * 2. Create a new project (React)
 * 3. Copy the DSN
 * 4. Add to .env: VITE_SENTRY_DSN=your_dsn_here
 * 5. Add to Vercel environment variables
 */

import * as Sentry from '@sentry/react';

export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.MODE || 'development';

  // Only initialize if DSN is provided
  if (!dsn) {
    if (import.meta.env.DEV) {
      console.log('Sentry: DSN not configured. Error tracking disabled.');
    }
    return;
  }

  try {
    Sentry.init({
      dsn,
      environment,
      
      // Performance Monitoring
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          // Session Replay for debugging
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],

      // Performance Monitoring
      tracesSampleRate: environment === 'production' ? 0.1 : 1.0, // 10% in prod, 100% in dev
      
      // Session Replay
      replaysSessionSampleRate: environment === 'production' ? 0.1 : 1.0,
      replaysOnErrorSampleRate: 1.0, // Always record on errors

      // Additional performance options
      enableTracing: true,
      enableLongTask: true,
      enableInp: true, // Interaction to Next Paint
      
      // Instrumentation
      instrumenter: 'sentry',

      // Release tracking
      release: import.meta.env.VITE_APP_VERSION || undefined,

      // Filter out known non-critical errors
      beforeSend(event, hint) {
        // Filter out browser extension errors
        if (event.exception) {
          const error = hint.originalException;
          if (error instanceof Error) {
            // Ignore common browser extension errors
            if (
              error.message.includes('chrome-extension://') ||
              error.message.includes('moz-extension://') ||
              error.message.includes('safari-extension://')
            ) {
              return null;
            }
          }
        }
        return event;
      },

      // Ignore specific errors
      ignoreErrors: [
        // Browser extensions
        'top.GLOBALS',
        'originalCreateNotification',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        'atomicFindClose',
        'fb_xd_fragment',
        'bmi_SafeAddOnload',
        'EBCallBackMessageReceived',
        // Network errors that are not actionable
        'NetworkError',
        'Network request failed',
        // ResizeObserver errors (common, not critical)
        'ResizeObserver loop limit exceeded',
      ],
    });

    if (import.meta.env.DEV) {
      console.log('Sentry: Initialized successfully');
    }
  } catch (error) {
    console.error('Sentry: Failed to initialize', error);
  }
};

/**
 * Capture exception manually
 */
export const captureException = (error: Error, context?: Record<string, any>) => {
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.captureException(error, {
      contexts: {
        custom: context || {},
      },
    });
  }
};

/**
 * Capture message manually
 */
export const captureMessage = (message: string, level: Sentry.SeverityLevel = 'info') => {
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.captureMessage(message, level);
  }
};

/**
 * Set user context
 */
export const setUser = (user: { id?: string; email?: string; username?: string }) => {
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.setUser(user);
  }
};

/**
 * Add breadcrumb
 */
export const addBreadcrumb = (breadcrumb: Sentry.Breadcrumb) => {
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.addBreadcrumb(breadcrumb);
  }
};

/**
 * Set context
 */
export const setContext = (name: string, context: Record<string, any>) => {
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.setContext(name, context);
  }
};

/**
 * Start a performance transaction
 */
export const startTransaction = (options: {
  name: string;
  op: string;
  tags?: Record<string, string>;
}): { finish: () => void } => {
  if (import.meta.env.VITE_SENTRY_DSN) {
    let span: Span | null = null;
    
    Sentry.startSpan(
      {
        name: options.name,
        op: options.op,
        attributes: options.tags || {},
      },
      (s) => {
        span = s;
      }
    );
    
    return {
      finish: () => {
        if (span) {
          span.end();
        }
      },
    };
  }
  
  // Return dummy transaction if Sentry is not configured
  return {
    finish: () => {},
  };
};

/**
 * Track performance metric
 */
export const trackPerformance = (metricName: string, value: number, unit: string = 'millisecond') => {
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.metrics.distribution(metricName, value, {
      unit,
    });
  }
};

